import { useCallback } from 'react';

import { ESearchFilters } from '@/constants/searchFilters';
import { useAppRoute } from '@/context/AppRouteContext';
import { IListingData } from '@/services/types/search/rentals/id';

import { useRouter } from './useRouter';

const QUERY_PARAMS_TO_CARRY_OVER = [
  ESearchFilters.ADDRESS,
  ESearchFilters.DATE_FROM,
  ESearchFilters.DATE_TO,
  ESearchFilters.FLEXIBLE_DAYS,
  ESearchFilters.GUESTS_ADULTS,
  ESearchFilters.GUESTS_CHILDREN,
  ESearchFilters.GUESTS_INFANTS,
  ESearchFilters.FILTER_FEATURE,
  ESearchFilters.FILTER_TYPE,
  ESearchFilters.PRICE_MIN,
  ESearchFilters.PRICE_MAX,
  ESearchFilters.DISCOUNT_CODE,
  ESearchFilters.DELIVERY,
  ESearchFilters.DELIVERY_STATIONARY,
  ESearchFilters.DELIVERY_CENTER,
  ESearchFilters.MAPBOX_ID,
  ESearchFilters.DELIVERY_LOCATION_ID,
  ESearchFilters.DELIVERY_QUERY,
  ESearchFilters.DELIVERY_DETAILS,
  ESearchFilters.DELIVERY_ADDRESS,
  ESearchFilters.SEARCH_DELIVERY_CAMPGROUND_ID,
] as string[];

export const useHandleBackToSearch = (
  listingData: IListingData | null,
  isStay?: boolean,
  isCampsite?: boolean,
  isAuto?: boolean,
) => {
  const { prev: appPreviousURL } = useAppRoute();
  const router = useRouter();

  const query = router.query;

  // We do not want to surface the campgrounds tab in production search
  // It's soft hidden behind filter[rental_category]=campground, which we don't want users getting to.
  // If you are at a campsite listing, take users back to stays tab.
  const rentalCategory = isCampsite || isStay ? 'stay' : 'rv';

  return useCallback(() => {
    // On server-side rendering (SSR), appPreviousURL is an empty string ('') because the navigation
    // starts fresh, but document.referrer will contain the correct URL from which the user navigated.
    // On the other hand, during client-side navigation, document.referrer may be incorrect as it
    // reflects the initial page load's referrer. In such cases, appPreviousURL is used instead.
    // Summary: Use appPreviousURL for client-side navigation and document.referrer for SSR.
    //
    // Special Handling:
    // If appPreviousURL includes /rv-search, it indicates a navigation within the search context, such as applying filters like dates.
    // On the listing page, when date filters are applied, the router updates the URL with date parameters.
    // As a result, appPreviousURL will point to the listing route instead of being an empty string.
    // To ensure consistency, prioritize appPreviousURL when it includes /rv-search. If not, fallback to document.referrer.
    const previousURL = appPreviousURL.pathname.includes(isAuto ? '/auto/car-search' : '/rv-search')
      ? appPreviousURL.asPath
      : document.referrer;
    let url;

    if (previousURL.includes(isAuto ? '/auto/car-search' : '/rv-search')) {
      url = previousURL;
    } else {
      // Carry over query params from the prev URL to the new URL
      const queryToCarryOver = QUERY_PARAMS_TO_CARRY_OVER.reduce(
        (acc: Record<string, string>, key: string) => {
          if (query[key]) {
            acc[key] = query[key] as string;
          }
          return acc;
        },
        {},
      );

      url = {
        pathname: isAuto ? '/auto/car-search' : '/rv-search',
        query: {
          ...queryToCarryOver,
          ...(!query[ESearchFilters.ADDRESS] &&
            listingData?.location && {
              [ESearchFilters.ADDRESS]: `${listingData.location.city}, ${listingData.location.state}`,
            }),
          ...(isAuto ? {} : { [ESearchFilters.RENTAL_CATEGORY]: rentalCategory }),
        },
      };
    }
    router.push(url, undefined, { shallow: true });
  }, [
    appPreviousURL.pathname,
    appPreviousURL.asPath,
    isAuto,
    router,
    rentalCategory,
    query,
    listingData?.location,
  ]);
};

export const useHandleShare = (
  description: string,
  title: string,
  isShareOpen: boolean,
  setIsShareOpen: (isShareOpen: boolean) => void,
  onShare: () => void,
) => {
  return useCallback(async () => {
    if (window?.navigator && window?.navigator.share) {
      try {
        await window.navigator.share({
          text: description,
          title: title,
          url: window.location.href,
        });
        onShare();
      } catch {
        return;
      }
    } else {
      if (isShareOpen) {
        onShare();
      }
      setIsShareOpen(!isShareOpen);
    }
  }, [description, isShareOpen, setIsShareOpen, onShare, title]);
};
